// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — fiche d'une campagne de vente subventionnée (moteur générique) :
//   • cycle de vie : préparer, simuler, soumettre ; valider (publier) par une AUTRE personne ; ouvrir, clôturer,
//     distribuer, terminer, solder, archiver ; refus et annulation motivés ;
//   • conditions du règlement (Centre de configuration) affichées telles que le serveur les normalise, simulation
//     budgétaire, comptes d'inscriptions, historique (auteur, fonction, intérim) ;
//   • vues : inscriptions, achats, attribution et réceptions, remises au guichet, écarts, état de campagne.
// Routes : /api/dispositifs/campagnes/:id (README du module, § 2). Le serveur reste seul juge de chaque acte.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Calculator, CheckCircle2, MoreVertical, PenLine, Play, ShieldCheck, Undo2, XCircle } from 'lucide-react';
import { Bouton, BandeauInfo, MenuActions, Squelette } from '../../design/composants';
import { useEspaces, droitsDans } from './espaces';
import { Champ, AccesRefuseDs, ErreurServeur } from './commun';
import { francs } from './apiSocial';
import {
  useLectureSociale, PageFiche, TitreSection, HistoriqueActes, FenetreActe, motifValide, ChampMotif, Tuiles, Infos, erreurSociale,
} from './communSocial';
import { useCampagnes, quantiteUnites, ETATS_CAMPAGNE, CYCLE_CAMPAGNE, ANNULABLE, ETATS_INSCRIPTION } from './apiCampagnes';
import {
  EtatCampagne, MarquesCampagne, BandeauxCampagne, ConditionsCampagne, ReglesAbsentes, LienCentre, porteeCampagne, codeEtat, nombreInscrits, unitesInscrites,
} from './communCampagnes';
import InscriptionsVue from './CampagneInscriptions';
import AchatsVue from './CampagneAchats';
import DistributionVue from './CampagneDistribution';
import RemisesVue from './CampagneRemises';
import { EcartsVue, EtatVue } from './CampagneEtat';
import { SuiviDossierGestion } from '../../components/suivi/SuiviDemande'; // prochain acteur (lecture)

/** Passages manuels par état (clé du serveur ; le valideur n'est jamais celui qui a préparé, simulé ou soumis). */
const PASSAGES = {
  BROUILLON: [{ vers: 'SIMULEE', cle: 'ds_campagne_preparer', libelle: 'Simuler', aide: 'La simulation calcule l\'engagement maximal et l\'aide maximale sur le règlement en vigueur ; elle est enregistrée dans l\'historique.' }],
  SIMULEE: [
    { vers: 'A_VALIDER', cle: 'ds_campagne_preparer', libelle: 'Soumettre à validation', aide: 'Le règlement doit être complet et validé au Centre de configuration. Une autre personne validera la campagne.' },
    { vers: 'BROUILLON', cle: 'ds_campagne_preparer', libelle: 'Revenir au brouillon' },
  ],
  A_VALIDER: [
    { vers: 'PUBLIEE', cle: 'ds_campagne_valider', libelle: 'Valider et publier', aide: 'Double regard : la personne qui a préparé, simulé ou soumis la campagne ne la valide pas. Les salariés éligibles sont prévenus.' },
    { vers: 'BROUILLON', cle: 'ds_campagne_valider', libelle: 'Refuser', motifRequis: true, danger: true, aide: 'La campagne revient en brouillon ; le motif est conservé dans l\'historique.' },
  ],
  PUBLIEE: [{ vers: 'OUVERTE', cle: 'ds_campagne_valider', libelle: 'Ouvrir les inscriptions', aide: 'Possible seulement pendant la période d\'inscription du règlement (l\'ouverture se fait aussi automatiquement à la date prévue).' }],
  OUVERTE: [{ vers: 'INSCRIPTIONS_CLOSES', cle: 'ds_campagne_valider', libelle: 'Clôturer les inscriptions', aide: 'Clôture anticipée possible ; sinon elle se fait automatiquement après la date de clôture.' }],
  INSCRIPTIONS_CLOSES: [{ vers: 'EN_DISTRIBUTION', cle: 'ds_campagne_valider', libelle: 'Commencer la distribution', aide: 'L\'attribution doit avoir été calculée par une personne et arrêtée par une autre.' }],
  EN_DISTRIBUTION: [{ vers: 'DISTRIBUEE', cle: 'ds_campagne_valider', libelle: 'Terminer la distribution', aide: 'Plus aucune remise ne sera possible.' }],
  DISTRIBUEE: [
    { vers: 'EN_REMBOURSEMENT', cle: 'ds_campagne_valider', libelle: 'Passer en remboursement' },
    { vers: 'SOLDEE', cle: 'ds_campagne_valider', libelle: 'Solder', aide: 'Refusé tant qu\'un remboursement reste en cours.' },
  ],
  EN_REMBOURSEMENT: [{ vers: 'SOLDEE', cle: 'ds_campagne_valider', libelle: 'Solder', aide: 'Refusé tant qu\'un remboursement reste en cours.' }],
  SOLDEE: [{ vers: 'ARCHIVEE', cle: 'ds_campagne_valider', libelle: 'Archiver' }],
};

/** États où le passage simulation → réel est permis (doublé côté serveur et en base, 0067). */
const PASSAGE_REEL_ETATS = ['BROUILLON', 'SIMULEE', 'A_VALIDER', 'PUBLIEE', 'OUVERTE', 'INSCRIPTIONS_CLOSES', 'EN_DISTRIBUTION'];

const VUES = [
  ['apercu', 'Aperçu'], ['inscriptions', 'Inscriptions'], ['achats', 'Achats'], ['distribution', 'Attribution et réceptions'],
  ['remises', 'Remises'], ['ecarts', 'Écarts'], ['etat', 'État de campagne'],
];

function FenetreTransition({ campagne, passage, onFermer, onFait }) {
  const api = useCampagnes();
  const [motif, setMotif] = useState('');
  const requis = !!passage.motifRequis;
  return (
    <FenetreActe titre={passage.libelle} Icone={passage.danger ? XCircle : CheckCircle2} libelleBouton={passage.libelle} danger={passage.danger}
      pret={requis ? motifValide(motif, 3) : (!motif.trim() || motifValide(motif, 3))} onFermer={onFermer} aide={passage.aide}
      onConfirmer={async () => { await api.transitionCampagne(campagne.id, passage.vers, motif.trim() || undefined); onFait(`${passage.libelle} : fait.`); }}>
      <ChampMotif valeur={motif} onChange={setMotif} requis={requis} libelleChamp={requis ? 'Motif' : 'Observation (facultative)'}
        aide={requis ? '3 caractères au moins. Le motif est conservé dans l\'historique.' : 'Conservée dans l\'historique.'} />
    </FenetreActe>
  );
}

function FenetreAnnuler({ campagne, onFermer, onFait }) {
  const api = useCampagnes();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre="Annuler la campagne" Icone={Undo2} libelleBouton="Annuler la campagne" danger pret={motifValide(motif, 5)} onFermer={onFermer}
      aide="Possible avant la distribution seulement. Les inscriptions en cours sont annulées (leurs numéros sont conservés) et les salariés prévenus ; aucune créance n'existe avant une remise. Les acomptes déjà versés aux fournisseurs restent à récupérer par la trésorerie."
      onConfirmer={async () => { await api.transitionCampagne(campagne.id, 'ANNULEE', motif.trim()); onFait('Campagne annulée.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

function FenetreModifier({ campagne, onFermer, onFait }) {
  const api = useCampagnes();
  const [lib, setLib] = useState(campagne.libelle);
  const [singulier, setSingulier] = useState(campagne.unite_singulier);
  const [pluriel, setPluriel] = useState(campagne.unite_pluriel);
  const pret = lib.trim().length >= 3 && singulier.trim().length >= 2 && pluriel.trim().length >= 2;
  return (
    <FenetreActe titre="Modifier la campagne" Icone={PenLine} libelleBouton="Enregistrer" pret={pret} onFermer={onFermer}
      aide="Possible en brouillon ou après simulation seulement : la campagne revient alors en brouillon."
      onConfirmer={async () => { await api.modifierCampagne(campagne.id, { libelle: lib.trim(), unite_singulier: singulier.trim(), unite_pluriel: pluriel.trim() }); onFait('Campagne modifiée.'); }}>
      <Champ libelle="Libellé" requis><input className="rh-champ-saisie" maxLength={80} value={lib} onChange={(e) => setLib(e.target.value)} /></Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Champ libelle="Unité (singulier)" requis><input className="rh-champ-saisie" maxLength={24} value={singulier} onChange={(e) => setSingulier(e.target.value)} /></Champ>
        <Champ libelle="Unité (pluriel)" requis><input className="rh-champ-saisie" maxLength={24} value={pluriel} onChange={(e) => setPluriel(e.target.value)} /></Champ>
      </div>
    </FenetreActe>
  );
}

/**
 * Passage de la simulation au réel (décision du 13/10) : préparer, puis confirmer ou rejeter par une AUTRE personne.
 * Bouton visible avec la clé ds_campagne_passer_reel seulement ; le serveur contrôle tout (clé, double regard, règlement
 * validé, motif). Les créances déjà nées en simulation le restent ; la Paie n'est pas déverrouillée.
 */
function FenetrePassageReel({ campagne, mode, onFermer, onFait }) {
  const api = useCampagnes();
  const [motif, setMotif] = useState('');
  const titres = { preparer: 'Préparer le passage au réel', confirmer: 'Confirmer le passage au réel', rejeter: 'Rejeter le passage au réel' };
  const aide = mode === 'preparer'
    ? 'Le règlement de la campagne doit être validé au Centre de configuration. Une AUTRE personne habilitée confirmera. Les créances déjà nées en simulation le resteront ; les retenues restent proposées et bloquées (aucune Paie réelle).'
    : 'Double regard : la personne qui a préparé le passage ne le confirme ni ne le rejette. Le motif est conservé dans l\'historique.';
  return (
    <FenetreActe titre={titres[mode]} Icone={mode === 'rejeter' ? XCircle : ShieldCheck} libelleBouton={titres[mode]} danger={mode === 'rejeter'}
      pret={motifValide(motif, mode === 'preparer' ? 5 : 3)} onFermer={onFermer} aide={aide}
      onConfirmer={async (cle) => {
        const f = { preparer: api.preparerPassageReel, confirmer: api.confirmerPassageReel, rejeter: api.rejeterPassageReel }[mode];
        await f(campagne.id, motif.trim(), cle);
        onFait({ preparer: 'Passage au réel préparé : une autre personne doit le confirmer.', confirmer: 'Campagne passée au réel.', rejeter: 'Passage au réel rejeté.' }[mode]);
      }}>
      <ChampMotif valeur={motif} onChange={setMotif} requis libelleChamp="Motif" aide="Obligatoire ; conservé dans l'historique et l'audit." />
    </FenetreActe>
  );
}

/** Frise du cycle de vie (état courant mis en avant ; une campagne annulée est signalée à part). */
function FriseCycle({ code }) {
  const rang = CYCLE_CAMPAGNE.indexOf(code);
  return (
    <ol aria-label="Cycle de vie de la campagne" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 0', listStyle: 'none', margin: '0 0 12px', padding: 0 }}>
      {CYCLE_CAMPAGNE.map((e, i) => {
        const courant = i === rang;
        const franchi = rang >= 0 && i < rang;
        return (
          <li key={e} aria-current={courant ? 'step' : undefined} style={{ display: 'inline-flex', alignItems: 'center', fontSize: 12, color: courant ? 'var(--sg-primaire)' : franchi ? 'var(--sg-texte-2)' : 'var(--sg-texte-4)', fontWeight: courant ? 600 : 400 }}>
            {i > 0 && <span aria-hidden="true" style={{ width: 12, height: 1, background: 'var(--sg-bord-fort)', margin: '0 6px' }} />}
            {ETATS_CAMPAGNE[e][1]}
          </li>
        );
      })}
    </ol>
  );
}

function SimulationBudgetaire({ campagne }) {
  const api = useCampagnes();
  const [res, setRes] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const lancer = async () => {
    setEnCours(true); setErreur(null);
    try { setRes(await api.simulationBudgetaire(campagne.id)); } catch (e) { setErreur(erreurSociale(e)); }
    setEnCours(false);
  };
  const m = res?.par_salarie_maximum;
  return (
    <div>
      <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Calcul du serveur sur le règlement en vigueur ; rien n'est enregistré.</p>
      <Bouton Icone={Calculator} disabled={enCours} onClick={lancer}>Simulation budgétaire</Bouton>
      <div style={{ marginTop: 12 }}>{erreur && (erreur.code === 'REGLE_ABSENTE' ? <ReglesAbsentes erreur={erreur} campagne={campagne} /> : <ErreurServeur erreur={erreur} />)}</div>
      {res && (
        <>
          <Tuiles tuiles={[
            { libelle: 'Engagement maximal', valeur: francs(res.engagement_maximal), aide: 'Quantité disponible × prix unitaire' },
            { libelle: 'Aide maximale', valeur: francs(res.aide_maximale), aide: `${res.salaries_eligibles} salarié(s) éligible(s)` },
            m && { libelle: 'Au plus par salarié', valeur: quantiteUnites(campagne, m.quantite), aide: `${francs(m.total)} − aide ${francs(m.aide)} = ${francs(m.reste)}` },
            m && { libelle: 'Montant mensuel maximal', valeur: francs(m.mensualite), aide: m.derniere !== m.mensualite ? `dernier mois : ${francs(m.derniere)}` : undefined },
          ]} />
          {res.quotite?.motif && <p className="rh-form-aide" style={{ margin: 0 }}>{res.quotite.motif}</p>}
        </>
      )}
    </div>
  );
}

function ComptesInscriptions({ campagne, comptes }) {
  const codes = Object.keys(comptes || {});
  if (!codes.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucune inscription pour l'instant.</p>;
  return (
    <>
      <p style={{ margin: '0 0 10px', fontSize: 14 }}>
        <strong>{nombreInscrits(comptes)}</strong> inscription(s) active(s) pour <strong>{quantiteUnites(campagne, unitesInscrites(comptes))}</strong>.
      </p>
      <Tuiles tuiles={codes.map((k) => ({ libelle: (ETATS_INSCRIPTION[k] || ['', k])[1], valeur: String(comptes[k].nombre), aide: quantiteUnites(campagne, comptes[k].quantite) }))} />
    </>
  );
}

function ReglementCampagne({ campagne }) {
  const api = useCampagnes();
  const conditions = campagne.reglement;
  // Règlement absent ou incomplet : la lecture du règlement à la date renvoie la liste des règles manquantes.
  const reg = useLectureSociale(() => (conditions ? Promise.resolve(null) : api.reglementCampagne(campagne.id)), [api, campagne.id, !!conditions]);
  return (
    <section className="rh-carte">
      <TitreSection action={<LienCentre portee={porteeCampagne(campagne)} />}>Conditions du règlement</TitreSection>
      {conditions ? (
        <>
          <ConditionsCampagne campagne={campagne} conditions={conditions} />
          <p className="rh-form-aide" style={{ margin: '12px 0 0' }}>
            Conditions en vigueur aujourd'hui. Chaque inscription fige les conditions du moment (prix, aide, durée, premier mois) : une nouvelle version du règlement
            ne s'applique jamais aux inscriptions déjà faites.
          </p>
        </>
      ) : (
        <>
          {reg.erreur && (reg.erreur.code === 'REGLE_ABSENTE' ? <ReglesAbsentes erreur={reg.erreur} campagne={campagne} /> : <ErreurServeur erreur={reg.erreur} />)}
          {!reg.erreur && !reg.donnees && <Squelette largeur="60%" />}
          {reg.donnees && <ConditionsCampagne campagne={campagne} conditions={reg.donnees.conditions} />}
        </>
      )}
    </section>
  );
}

function Apercu({ campagne }) {
  return (
    <>
      <ReglementCampagne campagne={campagne} />
      <section className="rh-carte">
        <TitreSection>Inscriptions</TitreSection>
        <ComptesInscriptions campagne={campagne} comptes={campagne.inscriptions} />
      </section>
      <section className="rh-carte">
        <TitreSection>Simulation budgétaire</TitreSection>
        <SimulationBudgetaire campagne={campagne} />
      </section>
      <section className="rh-carte">
        <TitreSection>Historique</TitreSection>
        <HistoriqueActes lignes={campagne.historique} etats={ETATS_CAMPAGNE} vide="Aucun acte enregistré." />
      </section>
    </>
  );
}

export default function CampagnePage() {
  const { org, id } = useParams();
  const api = useCampagnes();
  const { espaces } = useEspaces();
  const [params, setParams] = useSearchParams();
  const fiche = useLectureSociale(() => api.campagne(id), [api, id]);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const c = fiche.donnees;
  const droits = droitsDans(espaces, c?.organisation_id || org);
  const code = codeEtat(c);
  const vue = VUES.some(([k]) => k === params.get('vue')) ? params.get('vue') : 'apercu';
  const fait = (m) => { setFenetre(null); setInfo(m); fiche.recharger(); };
  const choisirVue = (k) => { setInfo(''); setParams(k === 'apercu' ? {} : { vue: k }, { replace: true }); };

  const passages = c ? (PASSAGES[code] || []).filter((p) => droits.a(p.cle)) : [];
  const [principal, ...autres] = passages;
  const menu = c ? [
    ...autres.map((p) => ({ libelle: p.libelle, Icone: p.danger ? XCircle : CheckCircle2, onClick: () => setFenetre({ type: 'transition', passage: p }) })),
    ['BROUILLON', 'SIMULEE'].includes(code) && droits.a('ds_campagne_preparer') && { libelle: 'Modifier', Icone: PenLine, onClick: () => setFenetre({ type: 'modifier' }) },
    ANNULABLE.includes(code) && droits.a('ds_campagne_valider') && { libelle: 'Annuler la campagne', Icone: Undo2, danger: true, onClick: () => setFenetre({ type: 'annuler' }) },
    // Passage simulation → réel : clé dédiée (masqué sans droit ; doublé côté serveur).
    ...(c.simulation && PASSAGE_REEL_ETATS.includes(code) && droits.a('ds_campagne_passer_reel')
      ? (c.passage_reel?.statut === 'PROPOSEE'
        ? [{ libelle: 'Confirmer le passage au réel', Icone: ShieldCheck, onClick: () => setFenetre({ type: 'reel', mode: 'confirmer' }) },
          { libelle: 'Rejeter le passage au réel', Icone: XCircle, danger: true, onClick: () => setFenetre({ type: 'reel', mode: 'rejeter' }) }]
        : [{ libelle: 'Passer au réel…', Icone: ShieldCheck, onClick: () => setFenetre({ type: 'reel', mode: 'preparer' }) }])
      : []),
  ].filter(Boolean) : [];
  const actions = c ? (
    <>
      {principal && <Bouton variante={principal.danger ? 'danger' : 'primaire'} Icone={Play} onClick={() => setFenetre({ type: 'transition', passage: principal })}>{principal.libelle}</Bouton>}
      {menu.length > 0 && <MenuActions iconeSeule Icone={MoreVertical} libelle="Autres actions" items={menu} />}
    </>
  ) : null;

  return (
    <PageFiche section="campagnes" libelleSection="Campagnes" titre={c ? c.libelle : 'Campagne'} actions={actions}>
      {fiche.erreur && (fiche.erreur.statut === 403 || fiche.erreur.statut === 404
        ? <section className="rh-carte"><AccesRefuseDs erreur={fiche.erreur} /></section>
        : <ErreurServeur erreur={fiche.erreur} action={<Bouton petit onClick={fiche.recharger}>Réessayer</Bouton>} />)}
      {!c && !fiche.erreur && <Squelette hauteur={160} />}
      {c && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '-8px 0 12px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
            <EtatCampagne etat={c.etat} /><span>{c.numero}</span><MarquesCampagne campagne={c} />
            {c.organisation?.libelle && <><span aria-hidden="true">·</span><span>{c.organisation.libelle}</span></>}
          </div>
          {code === 'ANNULEE' ? (
            <BandeauInfo ton="info" titre="Campagne annulée">{c.motif_annulation ? `Motif : ${c.motif_annulation}` : 'Consultation seule.'}</BandeauInfo>
          ) : <FriseCycle code={code} />}
          <BandeauxCampagne campagne={c} />
          {c.simulation && c.passage_reel?.statut === 'PROPOSEE' && (
            <BandeauInfo ton="info" titre="Passage au réel préparé">
              {c.passage_reel.motif ? `Motif : ${c.passage_reel.motif}. ` : ''}Une autre personne habilitée doit le confirmer ou le rejeter.
            </BandeauInfo>
          )}
          <SuiviDossierGestion type="DS_CAMPAGNE" id={id} cle={fiche.donnees} />
          {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
          <div className="rh-vues rh-vues-ligne" role="group" aria-label="Vues de la campagne">
            {VUES.map(([k, l]) => <button key={k} type="button" className="rh-vue" aria-pressed={vue === k} onClick={() => choisirVue(k)}>{l}</button>)}
          </div>
          {vue === 'apercu' && (
            <>
              <section className="rh-carte">
                <Infos lignes={[
                  ['Numéro', c.numero], ['Unités', `${c.unite_singulier} / ${c.unite_pluriel}`], ['Réservées', quantiteUnites(c, c.quantite_reservee)],
                  ['Validée', c.valide_le ? 'Oui, par une personne autre que le préparateur' : null],
                ]} />
              </section>
              <Apercu campagne={c} />
            </>
          )}
          {vue === 'inscriptions' && <InscriptionsVue campagne={c} droits={droits} />}
          {vue === 'achats' && <AchatsVue campagne={c} droits={droits} />}
          {vue === 'distribution' && <DistributionVue campagne={c} droits={droits} onChange={fiche.recharger} />}
          {vue === 'remises' && <RemisesVue campagne={c} droits={droits} />}
          {vue === 'ecarts' && <EcartsVue campagne={c} droits={droits} />}
          {vue === 'etat' && <EtatVue campagne={c} droits={droits} />}
        </>
      )}
      {fenetre?.type === 'transition' && <FenetreTransition campagne={c} passage={fenetre.passage} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'annuler' && <FenetreAnnuler campagne={c} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'modifier' && <FenetreModifier campagne={c} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'reel' && <FenetrePassageReel campagne={c} mode={fenetre.mode} onFermer={() => setFenetre(null)} onFait={fait} />}
    </PageFiche>
  );
}
